import { defineStore } from 'pinia'
import { computed, ref, shallowRef, type Component } from 'vue'

import { keys } from '@/app/i18n'

import { useUiStore } from './ui'

/**
 * The assistant's chrome state, read by the vertical layout (the main column keeps clear of the docked panel) and
 * driven by the assistant module's widget (src/modules/assistant): the pill at the bottom of the page is its only
 * closed state, the panel opens docked on the right and can grow into the expanded split, which collapses the nav to
 * its rail for the duration. The conversation itself is here too, so it survives navigation (the thread must not
 * disappear when the user opens a record and comes back). Replies come from a replier the widget registers: today the
 * module's offline engine, later the API.
 *
 * A **surface** may take the conversation over (`setSurface`, the AI mode's widget — docs/ai-mode): the panel keeps
 * its frame and header and renders the surface's body instead of its own thread and composer; the pill's messages,
 * "New conversation" and the thread title go to the surface. The surface can also put its **canvas** in place of the
 * page (`openCanvas`): the vertical layout then hides the nav and the page and leaves the content area to the
 * teleport target `ASSISTANT_CANVAS_TARGET_ID`. Modules never import each other: this store is the seam.
 */
export type AssistantMode = 'docked' | 'expanded'
/** How far the assistant may go on its own: reads only, reads and drafts, or everything. Writes always preview. */
export type AssistantAutonomy = 'ask' | 'drafts' | 'auto'

export interface AssistantAction {
  id: string
  /** i18n key (`assistant.*`). */
  label: string
  primary?: boolean
}
export interface AssistantStep {
  /** i18n key. */
  label: string
  done: boolean
}
export interface AssistantMessage {
  id: number
  role: 'user' | 'assistant'
  /** Plain text for a user message; an i18n key for an assistant message. */
  text: string
  /** Something the assistant changed on the page, with an undo (the "Filter applied" line); i18n key. */
  applied?: string
  /** A multi-step job's progress list. */
  steps?: AssistantStep[]
  /** The next-step card: a recap, one question, one or two answers. i18n keys. */
  nextStep?: { text: string; question: string; actions: AssistantAction[] }
  /** Still streaming in. */
  pending?: boolean
}
export interface AssistantAttachment {
  id: number
  kind: 'page' | 'file' | 'record'
  label: string
  /** The page chip follows the route; a file or a record stays until removed. */
  auto?: boolean
}
export interface AssistantThread {
  id: number
  /** i18n key or plain text. */
  title: string
  at: number
  messages: AssistantMessage[]
}
/** What the replier knows about the moment: the route, the attachments, and which suggestion or answer was picked. */
export interface AssistantContext {
  path: string
  attachments: readonly AssistantAttachment[]
  autonomy: AssistantAutonomy
  /** The id of the suggestion chip or next-step answer the user clicked, when the message came from one. */
  intent?: string
}
export type AssistantReply = Omit<AssistantMessage, 'id' | 'role'>
export type AssistantReplier = (text: string, context: AssistantContext) => Promise<AssistantReply>

/** A suggestion a surface offers the pill: plain text (the surface's own words), sent as `prompt`. */
export interface AssistantSurfaceSuggestion {
  id: string
  label: string
  hint: string
  prompt: string
}
/** A conversation that takes the panel over (the AI mode's chat): its body, and where the pill's messages go. */
export interface AssistantSurface {
  /** Rendered in the panel under its header, in place of the thread and the composer. */
  body: Component
  send: (text: string, intent?: string) => void
  /** "New conversation" in the header's history menu. */
  reset: () => void
  /** The header's subtitle: the thread's first question, or '' for a new one. */
  title: () => string
  /** The pill's suggestions (popover rows and the two chips); the route's offline ones when absent. */
  suggestions?: () => AssistantSurfaceSuggestion[]
}

/** The panel's width (a CSS length, read by the panel and the vertical layout's right margin). */
export const PANEL_WIDTH: Record<AssistantMode, string> = {
  docked: 'clamp(380px, 30vw, 440px)',
  expanded: 'clamp(460px, 38vw, 600px)',
}
/** The element of the vertical layout's content area a surface's canvas teleports into while it is open. */
export const ASSISTANT_CANVAS_TARGET_ID = 'assistant-canvas'

let nextId = 1

export const useAssistantStore = defineStore('assistant', () => {
  const ui = useUiStore()
  const open = ref(false)
  const mode = ref<AssistantMode>('docked')
  const autonomy = ref<AssistantAutonomy>('ask')
  const messages = ref<AssistantMessage[]>([])
  const threads = ref<AssistantThread[]>([])
  const attachments = ref<AssistantAttachment[]>([])
  const draft = ref('')
  const path = ref('/')
  const busy = ref(false)
  /** The widget is on the page (Clients and Staff in the vertical layout): the page keeps room for the pill. */
  const mounted = ref(false)
  /** The page has no room for the pill (the inbox's full-height frame, owner 2026-10-28): the panel still opens. */
  const pillHidden = ref(false)
  /** The nav's pin state before the expanded split collapsed it, restored on dock or close. */
  const navWasCollapsed = ref<boolean | null>(null)
  /** A surface's canvas stands in for the page (the vertical layout hides the nav and the page). */
  const canvasOpen = ref(false)
  const surface = shallowRef<AssistantSurface | null>(null)
  let replier: AssistantReplier | null = null

  const panelWidth = computed(() => PANEL_WIDTH[mode.value])
  const title = computed(() =>
    surface.value ? surface.value.title() : (messages.value.find((message) => message.role === 'user')?.text ?? ''),
  )
  const pageAttachment = computed(() => attachments.value.find((item) => item.kind === 'page') ?? null)

  function setReplier(fn: AssistantReplier | null): void {
    replier = fn
  }

  function setSurface(next: AssistantSurface | null): void {
    surface.value = next
    if (!next) canvasOpen.value = false
  }

  function openCanvas(): void {
    if (surface.value) canvasOpen.value = true
  }

  function closeCanvas(): void {
    canvasOpen.value = false
  }

  /** The route the assistant is looking at; the page chip follows it. */
  function setPage(routePath: string, label: string | null): void {
    path.value = routePath
    attachments.value = attachments.value.filter((item) => item.kind !== 'page')
    if (label) attachments.value.unshift({ id: nextId++, kind: 'page', label, auto: true })
  }

  function attach(kind: 'file' | 'record', label: string): void {
    attachments.value.push({ id: nextId++, kind, label })
  }

  function detach(id: number): void {
    attachments.value = attachments.value.filter((item) => item.id !== id)
  }

  function openPanel(): void {
    open.value = true
  }

  function closePanel(): void {
    dock()
    open.value = false
  }

  function expand(): void {
    if (mode.value === 'expanded') return
    mode.value = 'expanded'
    open.value = true
    navWasCollapsed.value = ui.navCollapsed
    ui.navCollapsed = true
  }

  function dock(): void {
    if (mode.value === 'docked') return
    mode.value = 'docked'
    if (navWasCollapsed.value !== null) ui.navCollapsed = navWasCollapsed.value
    navWasCollapsed.value = null
  }

  function toggleMode(): void {
    if (mode.value === 'docked') expand()
    else dock()
  }

  function setAutonomy(value: AssistantAutonomy): void {
    autonomy.value = value
  }

  /** Files the current thread under its first question and starts an empty one (a surface starts its own afresh). */
  function newThread(): void {
    if (surface.value) {
      surface.value.reset()
      return
    }
    if (messages.value.length) {
      threads.value.unshift({ id: nextId++, title: title.value, at: Date.now(), messages: messages.value })
      threads.value = threads.value.slice(0, 10)
    }
    messages.value = []
  }

  function resumeThread(id: number): void {
    const thread = threads.value.find((item) => item.id === id)
    if (!thread) return
    newThread()
    threads.value = threads.value.filter((item) => item.id !== id)
    messages.value = thread.messages
    open.value = true
  }

  /**
   * Sends a message: opens the panel, shows the user bubble, then the reply as the replier answers it. `intent` names
   * the suggestion or answer the words came from, so the replier need not parse them in 23 languages.
   */
  async function send(text: string, intent?: string): Promise<void> {
    const trimmed = text.trim()
    if (!trimmed || busy.value) return
    open.value = true
    draft.value = ''
    if (surface.value) {
      surface.value.send(trimmed, intent)
      return
    }
    messages.value.push({ id: nextId++, role: 'user', text: trimmed })
    const pending: AssistantMessage = { id: nextId++, role: 'assistant', text: '', pending: true }
    messages.value.push(pending)
    busy.value = true
    try {
      const context: AssistantContext = {
        path: path.value,
        attachments: attachments.value,
        autonomy: autonomy.value,
        intent,
      }
      const reply = replier ? await replier(trimmed, context) : { text: keys.t('assistant.mock.reply.unavailable') }
      const index = messages.value.findIndex((message) => message.id === pending.id)
      if (index >= 0) messages.value.splice(index, 1, { id: pending.id, role: 'assistant', ...reply })
    } finally {
      busy.value = false
    }
  }

  /** A next-step answer: sends the action's label as the user's words, so the thread reads as a dialogue. */
  function answer(messageId: number, action: AssistantAction, label: string): Promise<void> {
    const message = messages.value.find((item) => item.id === messageId)
    if (message?.nextStep) message.nextStep = undefined
    return send(label, action.id)
  }

  function undo(messageId: number): void {
    const message = messages.value.find((item) => item.id === messageId)
    if (message) message.applied = undefined
  }

  function reset(): void {
    closePanel()
    closeCanvas()
    messages.value = []
    threads.value = []
    attachments.value = []
    draft.value = ''
    busy.value = false
  }

  return {
    open,
    mode,
    autonomy,
    messages,
    threads,
    attachments,
    draft,
    path,
    busy,
    mounted,
    pillHidden,
    canvasOpen,
    surface,
    panelWidth,
    title,
    pageAttachment,
    setReplier,
    setSurface,
    openCanvas,
    closeCanvas,
    setPage,
    attach,
    detach,
    openPanel,
    closePanel,
    expand,
    dock,
    toggleMode,
    setAutonomy,
    newThread,
    resumeThread,
    send,
    answer,
    undo,
    reset,
  }
})
